import React, { useState, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import {
  useAssignSelectedClientsMutation,
  useGetAssignableStaffQuery,
  useGetTeamClientsQuery,
} from "../../../features/employeeSlice";
import { getStaffStatus, STAFF_STATUS } from "../../../Shared/staffStatus";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { colors } from "../../../Shared/colors";
import { toast, Toaster } from 'react-hot-toast';
import { Button, Grid, Box, Typography, Paper } from "@mui/material";

const columns = [
  {
    field: "fullName",
    headerName: "Full Name",
    width: 150,
    flex: 1,
    renderCell: (params) => {
      return (
        <Tooltip title={params.row.fullName}>
          <div>{params.row.fullName}</div>
        </Tooltip>
      );
    },
  },
  {
    field: "gender",
    headerName: "Gender",
    width: 100,
  },
  {
    field: "clientType",
    headerName: "Client Type",
    width: 150,
    flex: 1,
  },
  {
    field: "phone",
    headerName: "Phone",
    width: 150,
    flex: 1,
  },
  {
    field: "email",
    headerName: "Email",
    width: 200,
    flex: 1,
    renderCell: (params) => {
      return (
        <Tooltip title={params.row.email}>
          <div>{params.row.email}</div>
        </Tooltip>
      );
    },
  },
];

export const TeamTable = ({ teamId }) => {
  const { data: teamData, isFetching } = useGetTeamClientsQuery({ teamId });
  // Assignable, not active — this dialog is the assign step of Manage Team, so
  // a suspended colleague has to be reachable here too, or the row's "Choose
  // clients" button opens a picker that cannot find them. Each option carries
  // its status so the list says which is which.
  const { data: staff } = useGetAssignableStaffQuery();
  const [assignClients, { isLoading: isAssigning }] = useAssignSelectedClientsMutation();

  const [pageSize, setPageSize] = useState(5);
  const [selectedRows, setSelectedRows] = useState([])

  const options = useMemo(() => {
    return staff?.map((member) => ({
      id: member?._id,
      label: `${member?.firstName} ${member?.lastName}`,
      status: getStaffStatus(member),
    })) || [];
  }, [staff]);

  const [team, setteam] = useState(options[0]);

  let rows = useMemo(() => {
    let rows = [];
    teamData?.forEach((team) => {
      rows.push({
        id: team._id,
        fullName: team.fullName,
        gender: team.gender,
        email: team.email,
        clientType: team.clientType,
        phone: team?.cellNo[0]?.phoneNo
      });
    });
    return rows;
  }, [teamData]);

  const handleAssign = () => {
    if (!team) {
      toast.error("Please select a team member");
      return;
    }
    if (selectedRows.length === 0) {
      toast.error("Please select clients to assign");
      return;
    }

    let data = {
      clientIds: selectedRows
    }
    let id = team.id
    assignClients({ data, id }).unwrap()
      .then((res) => {
        toast.success("Clients Assigned Successfully")
        setteam(null)
        setSelectedRows([])
      })
      .catch((error) => {
        toast.error(error?.data?.error?.message || "Failed to assign clients")
      })
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: `1px solid ${colors.inputBorderWhite}` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Select Team Member to Assign
            </Typography>
            <Autocomplete
              disablePortal
              id="team-select"
              options={options}
              value={team}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(event, newvalue) => {
                setteam(newvalue);
              }}
              getOptionLabel={(option) => option.label || ""}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span>{option.label}</span>
                  {option.status === STAFF_STATUS.BLOCKED ? (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#8a6400",
                        background: "rgba(250, 188, 11, 0.16)",
                        borderRadius: 20,
                        padding: "2px 8px",
                      }}
                    >
                      Suspended
                    </span>
                  ) : null}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Search Staff..."
                  sx={{ bgcolor: 'white' }}
                />
              )}
            />
            {team?.status === STAFF_STATUS.BLOCKED ? (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.75, color: "#8a6400", fontWeight: 600 }}
              >
                {team.label} is suspended and cannot log in. The clients, leads
                and tasks will transfer now, and they will see them once the
                suspension is lifted.
              </Typography>
            ) : null}
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAssign}
              disabled={isAssigning}
              sx={{
                mt: 3.5,
                bgcolor: colors.brown,
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: colors.brown,
                  opacity: 0.9
                }
              }}
            >
              {isAssigning ? "Assigning..." : "Assign Selected Clients"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ flex: 1, width: '100%', minHeight: 400 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          onSelectionModelChange={(newSelectionModel) =>
            setSelectedRows(newSelectionModel)
          }
          selectionModel={selectedRows}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          checkboxSelection
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          loading={isFetching}
          autoHeight
        />
      </Box>
      <Toaster />
    </Box>
  );
};

const StyledDataGrid = styled(DataGrid)`
  &.MuiDataGrid-root {
    border: none;
    background-color: ${colors.white};
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
    border: 1px solid ${colors.inputBorderWhite};
  }

  .MuiDataGrid-columnHeaders {
    background: linear-gradient(to right, ${colors.lightWhite}, ${colors.white});
    border-bottom: 2px solid ${colors.brown};
    color: ${colors.brown};
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .MuiDataGrid-columnHeaderTitle {
    font-weight: 800;
  }

  .MuiDataGrid-row {
    border-bottom: 1px solid ${colors.inputBorderWhite};
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${colors.lightWhite};
    }
    
    &.Mui-selected {
      background-color: rgba(36, 147, 147, 0.08);
      border-left: 4px solid ${colors.brown};
       &:hover {
        background-color: rgba(36, 147, 147, 0.12);
      }
    }
  }

  .MuiDataGrid-footerContainer {
    border-top: 2px solid ${colors.inputBorderWhite};
    background-color: ${colors.white};
  }
  
  .MuiCheckbox-root {
    color: ${colors.inputBorderWhite};
    &.Mui-checked {
      color: ${colors.brown};
    }
  }
`;